'use client';

import { useTranslations } from 'next-intl';
import { type FC, type MouseEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { AgentLineHistory } from '@/app/components/icons/AgentLineHistory';
import { BaseLineSubmit2 } from '@/app/components/icons/BaseLineSubmit2';
import { HeadlessImageUploader } from '@/app/components/shared/ImageUploader';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { HeadlessVideoUploader } from '@/app/components/shared/VideoUploader/HeadlessVideoUploader';
import type { UploadScene } from '@/app/constants';
import type {
  FormImagePrompt,
  FormVideoPrompt,
  GetSelectedImageResult,
  ImageUploadFile,
  VideoUploadFile,
} from '@/app/types';
import { cn, getImageSize } from '@/app/utils';
import { ToolMediaUploadSurface } from './ToolMediaUploadSurface';

interface PreviewableImagePrompt extends NonNullable<FormImagePrompt['prompt']> {
  resolution?: {
    width?: number;
    height?: number;
  };
}

interface ToolMediaPreviewContentProps {
  type: 'image' | 'video';
  imagePrompt?: FormImagePrompt | null;
  videoPrompt?: FormVideoPrompt | null;
  showImageWatermark?: boolean;
}

interface ToolMediaHistoryActionButtonProps {
  onOpenSelector: () => void;
  className?: string;
}

interface ToolMediaActionButtonProps {
  className?: string;
  onClick?: () => void;
  children: ReactNode;
}

interface ToolMediaActionDividerProps {
  layout?: 'stacked' | 'inline';
  className?: string;
}

interface PreviewImageSize {
  width: number;
  height: number;
}

const DEFAULT_PREVIEW_IMAGE_SIZE: PreviewImageSize = {
  width: 16,
  height: 9,
};

const getPreviewImageSizeFromPrompt = (prompt?: PreviewableImagePrompt | null): PreviewImageSize | null => {
  if (prompt?.resolution?.width && prompt?.resolution?.height) {
    return {
      width: prompt.resolution.width,
      height: prompt.resolution.height,
    };
  }

  if (prompt?.selectedImageWidth && prompt?.selectedImageHeight) {
    return {
      width: prompt.selectedImageWidth,
      height: prompt.selectedImageHeight,
    };
  }

  if (prompt?.sourceImageWidth && prompt?.sourceImageHeight) {
    return {
      width: prompt.sourceImageWidth,
      height: prompt.sourceImageHeight,
    };
  }

  return null;
};

const getPreviewImageUrlFromPrompt = (prompt?: PreviewableImagePrompt | null): string => {
  return (
    prompt?.selectedImagePreview || prompt?.sourceImagePreview || prompt?.selectedImage || prompt?.sourceImage || ''
  );
};

const ToolMediaPreviewContent: FC<ToolMediaPreviewContentProps> = ({
  type,
  imagePrompt,
  videoPrompt,
  showImageWatermark = false,
}) => {
  const isVideo = type === 'video';
  const previewImagePrompt = imagePrompt?.prompt as PreviewableImagePrompt | null | undefined;
  const previewImageUrl = useMemo(() => getPreviewImageUrlFromPrompt(previewImagePrompt), [previewImagePrompt]);
  const promptPreviewImageSize = useMemo(() => getPreviewImageSizeFromPrompt(previewImagePrompt), [previewImagePrompt]);
  const [fallbackPreviewImageSize, setFallbackPreviewImageSize] = useState<PreviewImageSize | null>(null);

  useEffect(() => {
    if (promptPreviewImageSize || !previewImageUrl) {
      setFallbackPreviewImageSize(null);
      return;
    }

    let cancelled = false;

    void getImageSize(previewImageUrl)
      .then(({ width, height }) => {
        if (!cancelled && width > 0 && height > 0) {
          setFallbackPreviewImageSize({ width, height });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFallbackPreviewImageSize(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [previewImageUrl, promptPreviewImageSize]);

  const previewImageSize = promptPreviewImageSize || fallbackPreviewImageSize || DEFAULT_PREVIEW_IMAGE_SIZE;

  if (isVideo && videoPrompt?.prompt) {
    return (
      <video
        src={videoPrompt.prompt.videoPreview || ''}
        poster={videoPrompt.prompt.coverImagePreview}
        className="size-full object-contain"
        playsInline
        muted
        loop
        autoPlay
      >
        <track kind="captions" />
      </video>
    );
  }

  if (previewImagePrompt) {
    return (
      <div className="flex size-full items-center justify-center">
        <MediaWithWatermark
          width={previewImageSize.width}
          height={previewImageSize.height}
          className="relative"
          showWatermark={showImageWatermark}
        >
          <img src={previewImageUrl} alt="Uploaded" className="size-full object-contain" />
        </MediaWithWatermark>
      </div>
    );
  }

  return null;
};

const ToolMediaHistoryActionButton: FC<ToolMediaHistoryActionButtonProps> = ({ onOpenSelector, className }) => {
  const t = useTranslations();

  return (
    <button
      type="button"
      onClick={onOpenSelector}
      className={cn('group flex cursor-pointer items-center justify-center gap-2', className)}
    >
      <AgentLineHistory className="size-4 text-system-white" />
      <span className="font-normal text-system-white/40 text-xs leading-[1.4] group-hover:text-system-white">
        {t('tools.historyCreation')}
      </span>
    </button>
  );
};

const ToolMediaActionButton: FC<ToolMediaActionButtonProps> = ({ className, onClick, children }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex cursor-pointer items-center justify-center gap-2 px-5 py-4 transition-colors hover:bg-system-white/5',
        className,
      )}
    >
      {children}
    </button>
  );
};

const ToolMediaActionDivider: FC<ToolMediaActionDividerProps> = ({ layout = 'stacked', className }) => {
  return <div className={cn(layout === 'inline' ? 'h-8 w-px' : 'h-px w-[120px]', 'bg-system-white08', className)} />;
};

interface ToolMediaUploadProps {
  className?: string;
  surfaceClassName?: string;
  uploadActionClassName?: string;
  uploadIcon?: ReactNode;
  surfaceVariant?: 'default' | 'compact';
  surfaceSize?: 'sm' | 'md' | 'fill';
  type: 'image' | 'video';
  isMobile: boolean;
  onRemoveMedia: () => void;
  imagePrompt?: FormImagePrompt | null;
  maxFileSize?: number;
  maxDuration?: number;
  minDuration?: number;
  getSelectedImage?: (url: string) => Promise<GetSelectedImageResult>;
  validateImageResolution?: (imageResolution: { width: number; height: number }) => {
    errorMessage: string;
    errorReason: string;
  } | null;
  onImageUpload?: (file: ImageUploadFile) => void;
  videoPrompt?: FormVideoPrompt | null;
  maxVideoSize?: number;
  videoAccept?: string[];
  validateVideoMetadata?: (metadata: {
    duration: number;
    aspectRatio: number;
    width: number;
    height: number;
  }) => string | null;
  videoScene?: UploadScene;
  onVideoUpload?: (file: VideoUploadFile) => void;
  /** 在打开系统文件选择器前拦截上传请求，例如展示一次性上传规范提示。 */
  onRequestUpload?: (openFileSelector: () => void) => void;
  onOpenSelector?: () => void;
  showImageWatermark?: boolean;
  allowTempMode?: boolean;
  header: ReactNode;
  emptyExtraAction?: ReactNode;
  /**
   * 空状态下操作区的排列方式。
   * - 'stacked'（默认）：历史按钮在上，上传按钮在下（竖向分隔线）
   * - 'inline'：历史按钮与上传按钮水平排列（竖向分隔线）
   * - 'stackedExtra'：上传、历史和额外入口竖向排列
   */
  emptyActionsLayout?: 'stacked' | 'inline' | 'stackedExtra';
}

export const ToolMediaUpload: FC<ToolMediaUploadProps> = ({
  className,
  surfaceClassName: customSurfaceClassName,
  uploadActionClassName,
  uploadIcon,
  surfaceVariant = 'default',
  surfaceSize = 'md',
  type,
  isMobile,
  onRemoveMedia,
  imagePrompt,
  maxFileSize,
  maxDuration,
  minDuration,
  getSelectedImage,
  validateImageResolution,
  onImageUpload,
  videoPrompt,
  maxVideoSize,
  videoAccept,
  validateVideoMetadata,
  videoScene,
  onVideoUpload,
  onRequestUpload,
  onOpenSelector,
  showImageWatermark = false,
  allowTempMode = true,
  header,
  emptyExtraAction,
  emptyActionsLayout = 'stacked',
}) => {
  const t = useTranslations();
  const isVideo = type === 'video';
  const isUploading = isVideo ? videoPrompt?.status === 'uploading' : imagePrompt?.status === 'uploading';
  const hasMedia = isVideo
    ? videoPrompt?.status === 'done' && videoPrompt.prompt
    : imagePrompt?.status === 'done' && imagePrompt.prompt;

  const noopOpenFileSelector = async (_e?: MouseEvent) => {};

  const requestUpload = (openFileSelector: (e?: MouseEvent) => Promise<void>) => {
    const open = () => {
      void openFileSelector();
    };
    if (onRequestUpload) {
      onRequestUpload(open);
      return;
    }
    open();
  };

  const renderUploadButton = (openFileSelector: (e?: MouseEvent) => Promise<void>) => {
    if (isVideo && onVideoUpload) {
      return (
        <ToolMediaActionButton
          onClick={() => {
            requestUpload(openFileSelector);
          }}
          className={cn(
            uploadActionClassName,
            emptyActionsLayout === 'stacked' || emptyActionsLayout === 'stackedExtra'
              ? 'w-full rounded-8'
              : 'hover:rounded-8 sm:w-full',
          )}
        >
          {uploadIcon ?? <BaseLineSubmit2 className="size-4 text-system-white" />}
          <span className="text-system-white/40 text-xs leading-[1.4] group-hover:text-system-white">
            {t('tools.uploadNewFile')}
          </span>
        </ToolMediaActionButton>
      );
    }

    if (!isVideo && onImageUpload && getSelectedImage) {
      return (
        <ToolMediaActionButton
          onClick={() => {
            requestUpload(openFileSelector);
          }}
          className={cn(
            uploadActionClassName,
            'w-full rounded-[inherit]',
            emptyActionsLayout === 'stacked' || emptyActionsLayout === 'stackedExtra'
              ? 'rounded-8'
              : 'hover:rounded-8 sm:w-full',
          )}
        >
          {uploadIcon ?? <BaseLineSubmit2 className="size-4 text-system-white" />}
          <span className="text-system-white/40 text-xs leading-[1.4] group-hover:text-system-white">
            {t('tools.uploadNewFile')}
          </span>
        </ToolMediaActionButton>
      );
    }

    return null;
  };

  const renderDefaultEmptyContent = (openFileSelector: (e?: MouseEvent) => Promise<void>) => {
    if (emptyActionsLayout === 'inline') {
      return (
        <div className="flex size-full items-center justify-center rounded-12 sm:px-10">
          {onOpenSelector ? (
            <ToolMediaHistoryActionButton
              onOpenSelector={onOpenSelector}
              className="px-5 py-4 transition-colors hover:rounded-8 hover:bg-system-white/5 sm:flex-1"
            />
          ) : null}

          {onOpenSelector ? <ToolMediaActionDivider layout="inline" /> : null}

          <div className={cn(onOpenSelector && 'sm:flex-1')}>{renderUploadButton(openFileSelector)}</div>
        </div>
      );
    }

    if (emptyExtraAction && emptyActionsLayout === 'stackedExtra') {
      return (
        <div className="flex size-full items-center justify-center">
          <div className="flex flex-col items-center rounded-12">
            <div className="flex w-full items-center justify-center">{renderUploadButton(openFileSelector)}</div>
            <ToolMediaActionDivider />
            {onOpenSelector ? (
              <>
                <ToolMediaHistoryActionButton
                  onOpenSelector={onOpenSelector}
                  className="w-full rounded-8 px-5 py-4 transition-colors hover:bg-system-white/5"
                />
                <ToolMediaActionDivider />
              </>
            ) : null}
            <div className="flex w-full items-center justify-center">{emptyExtraAction}</div>
          </div>
        </div>
      );
    }

    if (emptyExtraAction) {
      return (
        <div className="flex size-full items-center justify-center">
          <div className="flex w-[min(260px,100%)] flex-col items-center rounded-12">
            <div className="flex w-full items-center justify-center">{renderUploadButton(openFileSelector)}</div>
            <ToolMediaActionDivider className="w-full" />
            <div className="flex w-full items-center justify-center">
              {onOpenSelector ? (
                <>
                  <ToolMediaHistoryActionButton
                    onOpenSelector={onOpenSelector}
                    className="flex-1 px-5 py-4 transition-colors hover:rounded-8 hover:bg-system-white/5"
                  />
                  <ToolMediaActionDivider layout="inline" className="h-6" />
                </>
              ) : null}
              <div className="flex-1">{emptyExtraAction}</div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex size-full items-center justify-center">
        <div className="flex flex-col items-center rounded-12">
          {onOpenSelector ? (
            <>
              <ToolMediaHistoryActionButton
                onOpenSelector={onOpenSelector}
                className="w-full rounded-8 px-5 py-4 transition-colors hover:bg-system-white/5"
              />
              <ToolMediaActionDivider />
            </>
          ) : null}

          <div className="flex w-full items-center justify-center">{renderUploadButton(openFileSelector)}</div>
        </div>
      </div>
    );
  };

  const surfaceClassName = cn(
    'relative flex min-h-0 w-full flex-col items-center justify-center border border-system-white08 bg-system-white04 backdrop-blur-[30px]',
    surfaceVariant === 'compact' ? 'rounded-12' : 'rounded-16',
    surfaceSize === 'sm' && 'h-[140px] max-h-[140px] shrink-0',
    surfaceSize === 'md' && 'h-[240px] max-h-[240px] shrink-0',
    surfaceSize === 'fill' && 'h-full flex-1',
    customSurfaceClassName,
  );

  const previewNode = (
    <ToolMediaPreviewContent
      type={type}
      imagePrompt={imagePrompt}
      videoPrompt={videoPrompt}
      showImageWatermark={showImageWatermark}
    />
  );

  const surfaceElement =
    isVideo && onVideoUpload ? (
      <HeadlessVideoUploader
        maxSize={maxVideoSize}
        maxDuration={maxDuration}
        minDuration={minDuration}
        videoAccept={videoAccept}
        validateMetadata={validateVideoMetadata}
        scene={videoScene}
        captureCoverImage={true}
        uploadCoverImage={true}
        allowTempMode={allowTempMode}
        emitUploadingInTempMode={false}
        onChange={onVideoUpload}
      >
        {({ openFileSelector }) => (
          <ToolMediaUploadSurface
            className={surfaceClassName}
            isUploading={isUploading}
            hasMedia={!!hasMedia}
            isMobile={isMobile}
            onRemoveMedia={onRemoveMedia}
            previewContent={previewNode}
            emptyContent={renderDefaultEmptyContent(openFileSelector)}
          />
        )}
      </HeadlessVideoUploader>
    ) : !isVideo && onImageUpload && getSelectedImage ? (
      <HeadlessImageUploader
        maxCount={1}
        maxFileSize={maxFileSize}
        validateImageResolution={validateImageResolution}
        getSelectedImage={getSelectedImage}
        onChange={onImageUpload}
        allowTempMode={allowTempMode}
        emitUploadingInTempMode={false}
      >
        {({ openFileSelector }) => (
          <ToolMediaUploadSurface
            className={surfaceClassName}
            isUploading={isUploading}
            hasMedia={!!hasMedia}
            isMobile={isMobile}
            onRemoveMedia={onRemoveMedia}
            previewContent={previewNode}
            emptyContent={renderDefaultEmptyContent(openFileSelector)}
            onEmptyClick={() => requestUpload(openFileSelector)}
          />
        )}
      </HeadlessImageUploader>
    ) : (
      <ToolMediaUploadSurface
        className={surfaceClassName}
        isUploading={isUploading}
        hasMedia={!!hasMedia}
        isMobile={isMobile}
        onRemoveMedia={onRemoveMedia}
        previewContent={previewNode}
        emptyContent={renderDefaultEmptyContent(noopOpenFileSelector)}
      />
    );

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-col gap-2', className)}>
      {header}
      {surfaceElement}
    </div>
  );
};
